import {IJackpotDropToastPresenter, IJackpotDropToastPresenterEnhancedOptions, IJackpotDropToastPresenterExtraOptions} from "./jackpot-drop-toast-presenter.interface";
import {TimeSpan} from "@sparkware/app-utils";
import {IServiceFactory} from "../../../service-factory.interface";
import classNames from "classnames";
import {ToastStylingClassNames} from "../../../toaster/toast-helpers/toast-classnames.helper";
import {JackpotToastComponent} from "../../../../components/jackpot-toast/jackpot-toast.component";
import {TrendingRecommendationTypeEnum} from "../../../ai-recommendations-reader/ai-recommendations-reader.service.interface";
import {KnownJackpotId} from "../../jackpot.constants";
import {imagePreload} from "../../../../../common/utils/image-preload";
import {makeObservable, observable, reaction} from "mobx";

export class DailyJackpotDropToastPresenter implements IJackpotDropToastPresenter<IJackpotDropToastPresenterExtraOptions> {
    constructor(private services: IServiceFactory) {
        makeObservable(this);

        this.services.gameRunner.then(gameRunner => {
            reaction(() => gameRunner.currentRunningGame, (currentRunningGame) => {
                this._isGameRunning = Boolean(currentRunningGame);
            }, {
                fireImmediately: true
            })
        });
    }

    @observable.ref
    private _imagePrerequisiteLoaded = false;

    @observable.ref
    private _isGameRunning = true;

    showToast(options: IJackpotDropToastPresenterEnhancedOptions<IJackpotDropToastPresenterExtraOptions>) {
        
        // 2. Identify the list of suggested games. Instead of taking the games from thresholdBatchConfig.gameTypes that comes from config and is usually an array with only one game,
        // (as it is done in JackpotThresholdToastPresenter)
        // we take the list of games from jackpot feeds that is passed here from JackpotModel
        // let gameTypes = thresholdBatchConfig.gameTypes ?? [];
        let gameTypes = [...options.gameTypes];
        if(gameTypes.length === 0) {
            this.services.logger.error(`Missing game types for jackpot toasts thresholds`);
            return null;
        }

        // 3. Take the gameTypes currated list, pass it to AI to order it and take the first 4 for suggestions
        gameTypes = this.services.aiEngine.sortGamesByAI(gameTypes, TrendingRecommendationTypeEnum.ActiveUsers)

        //filter only the games that are in the layout
        let gamesInfo = gameTypes.map(gt => this.services.games.gamesMap[gt])
            .filter(gameInfo => Boolean(gameInfo));

        if(gamesInfo.length === 0) {
            this.services.logger.warning(`None of the following game types ${gameTypes.join(",")} configured for jackpot toasts thresholds are in the layout` );
            return null;
        }

        gamesInfo = gamesInfo.filter(gInfo => gInfo.restriction.isGameAllowedToPlayReal())

        // take the first 4 elements, from index 0 (inclusive) to index 4 (exclusive)
        gamesInfo = gamesInfo.slice(0, 4);
        
        /* For the Logo, we need to map from jackpotId to logo */
        /* In widget, the jackpot type (that defines the images and styles) is defined in admin, however, here we don't have that information */
        const jpIdToLogoMap = {
            [KnownJackpotId.Daily]: this.services.theme.currentTheme.assets.images.jackpot_toast.daily_jp_toast_logo_png.toString(),
            [KnownJackpotId.Twilight]: this.services.theme.currentTheme.assets.images.jackpot_toast.twilight_jp_toast_logo_png.toString(),
            [KnownJackpotId.HourlyCaptain8]: this.services.theme.currentTheme.assets.images.jackpot_toast.power8_jp_toast_logo_png.toString()
        }
        const logoImage: string = jpIdToLogoMap[options.jackpotId];

        imagePreload(logoImage).finally(() => {
            this._imagePrerequisiteLoaded = true;
        });

        const mainTitle = this.services.translation.translate(options.titleText);
        const secondTitle = this.services.translation.translate(options.secondTitleText);
        const message = this.services.translation.translate("jackpot_toast_suggested_games_text");
        return this.services.toaster.showCustomToast({
            id: options.toastId,
            toastOptions: {
                timeout: TimeSpan.fromSeconds(15),
                className: classNames(
                    ToastStylingClassNames.ResetStyles,
                    ToastStylingClassNames.FrostedGlassBg,
                    ToastStylingClassNames.WithPadding,
                    ToastStylingClassNames.WithBorderLight,
                    ToastStylingClassNames.WithShadow
                ),
            },
            renderContent: (handler) => <JackpotToastComponent
                handler={handler}
                amount={options.winAmountInCents}
                mainTitle={mainTitle}
                secondTitle={secondTitle}
                message={message}
                logoImage={logoImage}
                gamesInfo={gamesInfo}
            />,
            displayCondition: () => {
                return this._imagePrerequisiteLoaded && !this._isGameRunning && !this.services.dialog.hasOpenDialogs;
            }
        });
    }

}